import { Box, Stack, Typography, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getColors, shadows } from "./payments.styles";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";

const MetricCard = ({ icon, iconBg, title, value, subtitle, subtitleIcon, trendType, theme }) => {
  const colors = getColors(theme);

  const getSubtitleColor = () => {
    if (trendType === "warning") return colors.warning;
    if (trendType === "up") return colors.success;
    return colors.textSecondary;
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: shadows.cardHover,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Row 1: Title (left) + Icon (right) */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            color: colors.textMuted,
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            bgcolor: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
      </Stack>

      {/* Row 2: Bold value */}
      <Typography
        sx={{
          fontSize: 28,
          fontWeight: 700,
          color: colors.textPrimary,
          mb: 1,
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>

      {/* Row 3: Subtitle with icon */}
      {subtitle && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {subtitleIcon}
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: getSubtitleColor(),
            }}
          >
            {subtitle}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

const PaymentMetrics = ({ data }) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const primaryColor = theme.palette.primary.main;

  const metrics = [
    {
      icon: <CurrencyRupeeIcon sx={{ fontSize: 20, color: colors.success }} />,
      iconBg: colors.successLight,
      title: "TOTAL REVENUE",
      value: `₹${data?.totalRevenue || "0"}`,
      subtitle: data?.revenueTrend || "0 total orders",
      subtitleIcon: <ShoppingCartIcon sx={{ fontSize: 14, color: colors.success }} />,
      trendType: "up",
    },
    {
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 20, color: colors.warning }} />,
      iconBg: colors.warningLight,
      title: "PENDING PAYMENTS",
      value: `₹${data?.pendingPayments || "0"}`,
      subtitle: data?.pendingCount || "0 invoices pending",
      subtitleIcon: <HourglassEmptyIcon sx={{ fontSize: 14, color: colors.warning }} />,
      trendType: "warning",
    },
    {
      icon: <ReceiptLongIcon sx={{ fontSize: 20, color: colors.success }} />,
      iconBg: colors.successLight,
      title: "TOTAL PAID",
      value: `₹${data?.totalPaid || "0"}`,
      subtitle: data?.paidCount || "0 invoices paid",
      subtitleIcon: <CheckCircleOutlineIcon sx={{ fontSize: 14, color: colors.success }} />,
      trendType: "up",
    },
    {
      icon: <ShowChartIcon sx={{ fontSize: 20, color: primaryColor }} />,
      iconBg: colors.primaryAlpha(10),
      title: "AVG PAID PER LEAD",
      value: `₹${data?.avgPaidPerLead || "0"}`,
      subtitle: data?.totalLeads || "0 total leads",
      subtitleIcon: <PeopleOutlineIcon sx={{ fontSize: 14, color: primaryColor }} />,
    },
  ];

  return (
    <Grid container spacing={3}>
      {metrics.map((metric, index) => (
        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
          <MetricCard {...metric} theme={theme} />
        </Grid>
      ))}
    </Grid>
  );
};

export default PaymentMetrics;
